import type { WorldData, SimState, MoveInput } from './types';
import { Simulation } from './simulation';
import { Monkey } from './character';
import { DemoPilot, type Demonstrations } from './pilot';
export class Troop {
    monkeys: Monkey[] = [];
    states: SimState[] = [];
    private pilots: DemoPilot[] = [];
    demos?: Demonstrations;
    get audit() { return this.pilots.map(p => ({ phase: p.phase, loops: p.loops, failures: p.failures })); }
    get followPoints() { const route = this.demos?.routes.find(r => r.id === 'safe'); if (!route)
        return []; const points: [
        number,
        number,
        number
    ][] = []; let last = -1; for (const s of route.states) {
        if (s[16] >= 0 && last !== s[16])
            points.push(s.slice(0, 3) as [
                number,
                number,
                number
            ]);
        last = s[16];
    } points.push([0, 14.65, 27]); return points; }
    private indices: number[] = [];
    private ages: number[] = [];
    private grabs: number[] = [];
    private racePoints: [
        number,
        number,
        number
    ][] = [];
    constructor(private sim: Simulation, private world: WorldData) {
        for (const name of ['home — village ropeway', 'village — temple ropeway', 'temple — cloud ropeway'])
            for (const o of world.objects.filter(o => o.name === name))
                this.racePoints.push([o.b[0], o.b[1] + .65, o.b[2]]);
    }
    reset() {
        this.pilots.forEach((p, i) => p.reset(i * .7));
        for (let i = 0; i < this.monkeys.length - 1; i++) {
            this.indices[i] = 0;
            this.ages[i] = 0;
            if (!this.pilots[i])
                this.sim.spawn(i + 1, [i * 1.1 - 1.5, 14.65, -1]);
        }
    }
    async load() {
        this.demos = await fetch('/world/demos.json').then(r => { if (!r.ok)
            throw Error('Demo routes unavailable'); return r.json(); });
        if (this.demos?.physicsVersion !== this.sim.version)
            throw Error('NPC routes need rebuilding for this physics version.');
        for (let i = 0; i < this.world.npcs.length; i++) {
            const n = this.world.npcs[i], monkey = await Monkey.create(n.character);
            this.monkeys.push(monkey);
            const p = i === 4 ? [0, 14.65, 27] : [i * 1.5 - 3, 14.65, 1];
            this.sim.spawn(i + 1, p as [
                number,
                number,
                number
            ]);
            this.indices.push(0);
            this.ages.push(0);
            this.grabs.push(0);
            this.states.push(this.sim.read(i + 1));
            if (i < 3) {
                const route = this.demos!.routes.find(r => r.id === n.route)!;
                this.pilots.push(new DemoPilot(this.sim, i + 1, route, this.world, i * .7));
            }
        }
    }
    step(player: SimState, mode: string) {
        for (let i = 0; i < this.monkeys.length; i++) {
            if (i < 3 && !['race', 'tag', 'golden'].includes(mode)) {
                this.pilots[i].step(player, mode === 'follow' && i === 0);
                this.states[i] = this.sim.read(i + 1);
                continue;
            }
            const n = this.world.npcs[i], route = this.world.routes.find(r => r.id === n.route)!;
            let s = this.sim.read(i + 1);
            const points = mode === 'race' ? this.racePoints : i === 3 ? this.world.objects.filter(o => o.name === 'home — village ropeway').map(o => [o.b[0], o.b[1] + .65, o.b[2]] as [
                number,
                number,
                number
            ]) : route.points;
            let goal = points[Math.min(this.indices[i], points.length - 1)];
            if (i === 4) {
                this.sim.step(i + 1, { x: 0, z: 0, buttons: 0, assist: 0 });
                continue;
            }
            if (mode === 'tag' || (mode === 'golden' && i === 1)) {
                const direction = mode === 'tag' ? -1 : 1;
                goal = [s.p[0] + (player.p[0] - s.p[0]) * direction, s.p[1], s.p[2] + (player.p[2] - s.p[2]) * direction];
            }
            const dx = goal[0] - s.p[0], dz = goal[2] - s.p[2], d = Math.hypot(dx, dz);
            this.ages[i] += 1 / 120;
            if (d < 2) {
                this.indices[i] = mode === 'race' ? Math.min(points.length - 1, this.indices[i] + 1) : (this.indices[i] + 1) % points.length;
                this.ages[i] = 0;
            }
            let buttons = mode === 'race' || i === 3 ? 0 : s.mode === 0 ? 4 : 1;
            if (s.grab >= 0) {
                this.grabs[i] += 1 / 120;
                if (this.grabs[i] > .3 && (s.v[0] * dx + s.v[2] * dz > 2 || this.grabs[i] > .8))
                    buttons = 16;
            }
            else
                this.grabs[i] = 0;
            if (mode === 'follow' && i === 0 && Math.hypot(player.p[0] - s.p[0], player.p[2] - s.p[2]) > 12)
                buttons = 1;
            const wait = mode === 'follow' && i === 0 && Math.hypot(player.p[0] - s.p[0], player.p[2] - s.p[2]) > 12;
            const input: MoveInput = { x: wait ? 0 : dx / Math.max(1, d), z: wait ? 0 : dz / Math.max(1, d), buttons, assist: 0 };
            this.sim.step(i + 1, input);
            s = this.sim.read(i + 1);
            this.states[i] = s;
        }
    }
    update(dt: number, player: SimState, carryingBaby: boolean) {
        for (let i = 0; i < this.monkeys.length; i++) {
            let s = this.sim.read(i + 1);
            this.states[i] = s;
            if (i === 4 && carryingBaby)
                s = { ...s, p: [player.p[0], player.p[1] + .2, player.p[2] - .3], mode: 0 };
            const m = this.monkeys[i];
            m.root.visible = Math.hypot(player.p[0] - s.p[0], player.p[2] - s.p[2]) < 90;
            const yaw = Math.hypot(s.v[0], s.v[2]) > .5 ? Math.atan2(s.v[0], s.v[2]) : 0;
            m.update(dt, s, yaw);
        }
    }
}
